import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  UserPlusIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { mutualisteAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const AdminMutualistesList = () => {
  const [mutualistes, setMutualistes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [gotoPage, setGotoPage] = useState('');

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMutualistes();
    }, 500); // Attendre 500ms après la dernière frappe
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch immédiat pour les changements de page et filtre
  useEffect(() => {
    fetchMutualistes();
  }, [currentPage, statusFilter]);

  const fetchMutualistes = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };
      
      const response = await mutualisteAPI.getAll(params);
      console.log('Response from API:', response.data);
      setMutualistes(response.data.mutualistes || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Erreur lors du chargement des mutualistes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statusObj) => {
    // Gérer le cas où status est un objet ou une chaîne
    const statusLabel = statusObj?.libelle || statusObj || 'N/A';
    
    // Normaliser le libellé pour la comparaison (enlever les variations)
    const normalizedLabel = statusLabel.toLowerCase().replace(/[()]/g, '').trim();
    
    // Utiliser le libellé normalisé pour déterminer les couleurs
    let config = { bg: 'bg-gray-100', text: 'text-gray-800' }; // Par défaut
    
    if (normalizedLabel.includes('actif')) {
      config = { bg: 'bg-green-100', text: 'text-green-800' };
    } else if (normalizedLabel.includes('suspendu')) {
      config = { bg: 'bg-yellow-100', text: 'text-yellow-800' };
    } else if (normalizedLabel.includes('radié') || normalizedLabel.includes('radie')) {
      config = { bg: 'bg-red-100', text: 'text-red-800' };
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {statusLabel}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getPaginationItems = (current, total, maxVisible = 9) => {
    const items = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) items.push(i);
      return items;
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - half);
    let end = start + maxVisible - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) items.push(i);

    return items;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="flex items-center space-x-4">
        <Link
          to="/admin/mutualistes"
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la gestion mutualiste
        </Link>
      </div>

      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-primary-900">
              Liste des Mutualistes
            </h1>
            <p className="text-gray-600 mt-1">
              Gérer tous les mutualistes inscrits dans le système
            </p>
          </div>
          <Link
            to="/admin/mutualistes/nouveau"
            className="btn-primary flex items-center"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            Nouveau Mutualiste
          </Link>
        </div>

        {/* Filtres et recherche */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="ACTIF">Actifs</option>
                <option value="SUSPENDU">Suspendus</option>
                <option value="RADIE">Radiés</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des mutualistes */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Matricule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mutualiste
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date d'inscription
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mutualistes.map((mutualiste) => (
                <tr key={mutualiste.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {mutualiste.matricule}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {mutualiste.prenoms?.charAt(0)}{mutualiste.nom?.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {mutualiste.prenoms} {mutualiste.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {mutualiste.type === 'ADHERENT' ? 'Adhérent' : 'Ayant-droit'} • {mutualiste.genre?.libelle || mutualiste.genre || 'Genre non renseigné'} • {mutualiste.dateNaissance ? 
                            new Date().getFullYear() - new Date(mutualiste.dateNaissance).getFullYear() + ' ans' 
                            : 'Âge non renseigné'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(mutualiste.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(mutualiste.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/admin/mutualistes/${mutualiste.id}`}
                        className="text-primary-600 hover:text-primary-900"
                        title="Consulter"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/admin/mutualistes/${mutualiste.id}/modifier`}
                        className="text-gray-600 hover:text-gray-900"
                        title="Modifier"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> sur{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={gotoPage}
                    onChange={(e) => setGotoPage(e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Aller à"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const pageNumber = parseInt(gotoPage, 10);
                      if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
                        setCurrentPage(pageNumber);
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Aller
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Préc.
                </button>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {getPaginationItems(currentPage, totalPages).map((item, index) => {
                    if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                      return (
                        <button
                          key={item + index}
                          type="button"
                          onClick={() => {
                            if (item === 'left-ellipsis') {
                              setCurrentPage(Math.max(1, currentPage - 10));
                            } else {
                              setCurrentPage(Math.min(totalPages, currentPage + 10));
                            }
                          }}
                          className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                          title={item === 'left-ellipsis' ? 'Reculer de plusieurs pages' : 'Avancer de plusieurs pages'}
                        >
                          ...
                        </button>
                      );
                    }

                    const page = item;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </nav>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Suiv.
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message si aucun résultat */}
      {mutualistes.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-card p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun mutualiste trouvé
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Aucun mutualiste ne correspond à vos critères de recherche.'
              : 'Commencez par inscrire votre premier mutualiste.'}
          </p>
          <Link
            to="/admin/mutualistes/nouveau"
            className="btn-primary inline-flex items-center"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            Inscrire un mutualiste
          </Link>
        </div>
      )}
    </div>
  );
};

export default AdminMutualistesList;
